import { FormSelect, InputGroup } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAnglesLeft, faMagnifyingGlassMinus } from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import { NewsEvent } from '@/modules/calendar/model/calendar/news-event';
import { DataRelease } from '@/modules/calendar/model/calendar/data-release';
import { DataSeries } from '@/modules/calendar/model/calendar/data-series';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartData,
  ChartDataset,
  ChartOptions,
  Plugin,
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import annotationPlugin, { AnnotationOptions } from 'chartjs-plugin-annotation';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { getDeepDiveApp } from '@/core/deep-dive/export';
import { useWidgetPermission } from '@/core/widgets/export';
import { HSC_WIDGET_ID } from '@/modules/calendar/widgets/hsc-widget-id';
import '@/modules/calendar/components/data/data-release-history-chart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  zoomPlugin,
  annotationPlugin,
);

type Data = {
  date: string;
  value?: string;
  forecast?: string;
  revision?: string;
};

function getRevision(series: DataSeries, date: string, events: NewsEvent[]): string | undefined {
  if (series.related) {
    const target = series.related.find(
      series => series.relation === 'HAS_PREVIOUS_REVISED_BY',
    )?.target;

    const event = events
      .sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
      .find(value => {
        return dayjs(value.date).isAfter(dayjs(date));
      });

    const entries = event?.dataSeriesEntries;

    if (entries) {
      return entries.find(e => e.dataSeriesId === target)?.actual;
    }
  }
}

export default function DataReleaseHistoryChart({
  events,
  selectedRelease,
  highlightDate,
  dedicatedView,
  canExpandHistory,
  onExpandHistory,
}: {
  events: NewsEvent[];
  selectedRelease: DataRelease;
  highlightDate?: string;
  dedicatedView?: boolean;
  canExpandHistory?: boolean;
  onExpandHistory?: () => void;
}) {
  const openWidget = useOpenWidget();
  const [selected, setSelected] = useState<number>();
  const [data, setData] = useState<ChartData<'line'>>();
  const [selectedSeries, setSelectedSeries] = useState<DataSeries | undefined>();
  const [gridColor, setGridColor] = useState<string>('grey');
  const [yearAnnotations, setYearAnnotations] = useState<Record<string, AnnotationOptions>>({});
  const [chartArea, setChartArea] = useState<{ top: number; bottom: number; left: number }>();

  // Looked up rather than imported: the HSC widget's routes render this chart.
  const { authorized: hscAuthorized } = useWidgetPermission(getDeepDiveApp(HSC_WIDGET_ID));
  const chartRef = useRef<ChartJS<'line'> | null>(null);

  const chartAreaSyncPlugin = useMemo<Plugin<'line'>>(
    () => ({
      id: 'expand-history-chart-area-sync',
      afterLayout: chart => {
        const { top, bottom, left } = chart.chartArea;
        setChartArea(prev =>
          prev && prev.top === top && prev.bottom === bottom && prev.left === left
            ? prev
            : { top, bottom, left },
        );
      },
    }),
    [],
  );

  const chartOptions = useMemo<ChartOptions<'line'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      scales: {
        x: {
          grid: {
            color: gridColor,
          },
        },
        y: {
          grid: {
            color: gridColor,
          },
        },
      },
      plugins: {
        legend: {
          position: 'bottom' as const,
        },
        title: {
          display: true,
          text: selectedSeries?.display,
        },
        tooltip: {
          mode: 'index',
        },
        annotation: {
          annotations: yearAnnotations,
        },
        zoom:
          dedicatedView && hscAuthorized
            ? {
                zoom: {
                  wheel: { enabled: true },
                  pinch: { enabled: true },
                  mode: 'x',
                },
                pan: {
                  enabled: true,
                  mode: 'x',
                },
              }
            : undefined,
      },
      onHover: (chartEvent, elements) => {
        const target = chartEvent.native?.target as HTMLElement | null;
        if (!target) return;
        const canNavigate = dedicatedView && hscAuthorized;
        target.style.cursor = canNavigate && elements.length ? 'pointer' : 'default';
      },
      onClick: (_, elements) => {
        if (!dedicatedView || !hscAuthorized || !elements.length) return;
        const clickedEvent = events[elements[0].index];
        if (clickedEvent) openWidget(HSC_WIDGET_ID, `/event/${clickedEvent.id}`);
      },
    }),
    [gridColor, selectedSeries, yearAnnotations, dedicatedView, hscAuthorized, events, openWidget],
  );

  useEffect(() => {
    setSelected(selectedRelease.series[0].id);
  }, [selectedRelease]);

  useEffect(() => {
    if (!selectedRelease) return;
    const data: Data[] = [];
    const series = selectedRelease.series.find(s => s.id === selected);

    setSelectedSeries(series);
    const sortedEvents = events.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

    const highlightMoment = highlightDate ? dayjs(highlightDate) : undefined;
    const highlightIndex = highlightMoment
      ? sortedEvents.findIndex(event => dayjs(event.date).isSame(highlightMoment))
      : -1;

    sortedEvents.forEach(event => {
      let revision: string | undefined;

      if (series) {
        revision = getRevision(series, event.date, events);
      }

      data.push({
        date: dayjs(event.date).format('MMM D') + ' (' + event.reportingPeriod + ')',
        value: event.dataSeriesEntries.find(value => `${value.dataSeriesId}` === `${selected}`)
          ?.actual,
        forecast: event.dataSeriesEntries
          .find(value => `${value.dataSeriesId}` === `${selected}`)
          ?.forecast()?.value,
        revision,
      });
    });

    const style = getComputedStyle(document.body);
    const primCol = style.getPropertyValue('--bs-info');
    const revColor = style.getPropertyValue('--bs-success');
    const forecastColor = style.getPropertyValue('--bs-tertiary');
    const gridColor = style.getPropertyValue('--bs-border-color');
    const yearDividerColor = style.getPropertyValue('--bs-warning');

    setGridColor(gridColor);

    const yearAnnotations: Record<string, AnnotationOptions> = {};
    let previousYear: number | undefined;

    sortedEvents.forEach((event, index) => {
      const year = dayjs(event.date).year();

      if (previousYear !== undefined && year !== previousYear) {
        yearAnnotations[`year-${year}`] = {
          type: 'line',
          scaleID: 'x',
          value: index,
          borderColor: yearDividerColor,
          borderWidth: 2,
          label: {
            display: true,
            content: `${year}`,
            position: 'start',
            color: yearDividerColor,
            font: { weight: 'bold', size: 11 },
            backgroundColor: 'transparent',
            xAdjust: 24,
            yAdjust: 4,
          },
        } as AnnotationOptions;
      }

      previousYear = year;
    });

    setYearAnnotations(yearAnnotations);

    const pointRadius = data.map((_, index) => (index === highlightIndex ? 8 : 3));
    const pointHoverRadius = data.map((_, index) => (index === highlightIndex ? 10 : 5));

    const chartData: ChartData<'line'> = {
      labels: data.map(d => d.date),
      datasets: [],
    };

    if (data.find(d => d.forecast)) {
      chartData.datasets.push({
        label: 'Forecast',
        data: data.map(value => parseFloat(value.forecast || '')),
        borderColor: forecastColor,
        borderDash: [5, 5],
        borderWidth: 1,
        spanGaps: true,
        cubicInterpolationMode: 'monotone',
        pointRadius,
        pointHoverRadius,
      } as ChartDataset<'line'>);
    }

    chartData.datasets.push({
      label: 'Actual',
      data: data.map(value => parseFloat(value.value || '')),
      borderColor: primCol,
      borderWidth: 2,
      cubicInterpolationMode: 'monotone',
      pointRadius,
      pointHoverRadius,
    } as ChartDataset<'line'>);

    if (data.find(d => d.revision)) {
      chartData.datasets.push({
        label: 'Revision',
        data: data.map(value => parseFloat(value.revision || '')),
        borderColor: revColor,
        cubicInterpolationMode: 'monotone',
        pointRadius,
        pointHoverRadius,
      } as ChartDataset<'line'>);
    }

    setData(chartData);
  }, [selected, events, selectedRelease, highlightDate]);
  return (
    <>
      <div className={'mb-3'}>
        <InputGroup>
          <FormSelect
            aria-label="Select Data Release"
            onChange={e => setSelected(parseInt(e.target.value))}
            size={'sm'}
          >
            {selectedRelease.series
              .filter(
                s =>
                  !(
                    s.related &&
                    s.related.find(r => r.relation.toUpperCase() === 'IS_PREVIOUS_REVISION_OF')
                  ),
              )
              .map(series => (
                <option key={series.id} value={series.id}>
                  {series.display}
                </option>
              ))}
          </FormSelect>
        </InputGroup>
      </div>
      <div className={'mb-3 w-100 position-relative'} style={{ height: '300px' }}>
        {data && dedicatedView && (
          <button
            type={'button'}
            className={'btn btn-sm btn-link position-absolute top-0 end-0 z-1'}
            aria-label={'Reset zoom'}
            title={'Reset zoom'}
            onClick={() => chartRef.current?.resetZoom()}
          >
            <FontAwesomeIcon icon={faMagnifyingGlassMinus} size={'xs'} />
          </button>
        )}
        {data && dedicatedView && onExpandHistory && (
          <button
            type={'button'}
            className={'expand-history-button'}
            style={{
              top: chartArea?.top,
              height: chartArea ? chartArea.bottom - chartArea.top : undefined,
              width: chartArea?.left,
            }}
            aria-label={'Load earlier history'}
            title={'Load 6 more months of history'}
            disabled={!canExpandHistory}
            onClick={onExpandHistory}
          >
            <span className={'expand-history-button-bg'} style={{ color: gridColor }} />
            <FontAwesomeIcon
              icon={faAnglesLeft}
              size={'xs'}
              className={'expand-history-button-icon'}
            />
          </button>
        )}
        {data && (
          <Line ref={chartRef} data={data} plugins={[chartAreaSyncPlugin]} options={chartOptions} />
        )}
      </div>
      {dedicatedView && hscAuthorized && (
        <>
          <div className={'text-muted small text-center'}>
            Click on an historic event to see its details.
          </div>
          <div className={'text-muted small text-center'}>Scroll or pinch to zoom in.</div>
        </>
      )}
    </>
  );
}
